import React, { type FC, Fragment } from 'react'
import { List, ListItem, ListItemIcon, ListItemText, type SxProps, type Theme } from '@mui/material'
import { Star } from '@mui/icons-material'

import { objectEntries, type ReadonlyRecord } from '../../../utils'

import {
    type CybusConfiguredResources,
    type CybusDetailedService,
    doesServiceNeedAuthorization,
    ResourceType,
    selectEnabableLoadedDetailedService,
    Translation,
} from '../../../domain'

import { useTranslator } from '../Internationalization'
import { EnableResourceModal } from '../Resources'
import { useAppUsecase } from '../State'

const translations: ReadonlyRecord<keyof CybusConfiguredResources, Translation> = {
    connections: Translation.CONNECTION,
    containers: Translation.CONTAINER,
    endpoints: Translation.ENDPOINT,
    routes: Translation.ROUTE,
    mappings: Translation.MAPPING,
    roles: Translation.ROLE,
    servers: Translation.SERVER,
    nodes: Translation.NODE,
    users: Translation.USER,
    volumes: Translation.VOLUME,
}

const listItemStyle: SxProps<Theme> = { listStyle: 'outside' }
const noMarginStyle: SxProps<Theme> = { m: 0 }

const Content: FC<{ resource: CybusDetailedService }> = ({ resource: { configuredResources } }) => {
    const translator = useTranslator()

    return (
        <List dense disablePadding sx={listItemStyle}>
            {objectEntries(configuredResources).map(([resource, count]) => {
                if (count === 0) {
                    return null
                }

                return (
                    <ListItem key={resource} alignItems="flex-start" sx={listItemStyle}>
                        <ListItemIcon sx={noMarginStyle}>
                            <Star />
                        </ListItemIcon>
                        <ListItemText data-testid={`service-${resource}`} primaryTypographyProps={{ variant: 'body1' }} sx={noMarginStyle}>
                            {translator.formatTranslation(Translation.AUTHORIZATION_REQUIRED_DOCUMENTATION_BODY_ITEM, {
                                count,
                                name: translator.formatTranslation(translations[resource], { count }),
                            })}
                        </ListItemText>
                    </ListItem>
                )
            })}
        </List>
    )
}

export const EnablingAuthorizationModal: FC = () => {
    const usecase = useAppUsecase('bulkEnableServiceUsecase')

    return (
        <EnableResourceModal<CybusDetailedService>
            title={Translation.AUTHORIZATION_REQUIRED}
            subtitle={Translation.AUTHORIZE_SERVICE_REQUIREMENTS}
            infoBody={Translation.AUTHORIZATION_REQUIRED_DOCUMENTATION_BODY}
            confirm={Translation.ALLOW}
            resourceSelector={selectEnabableLoadedDetailedService}
            dimension={ResourceType.SERVICE}
            onRenderLine={Fragment}
            onRenderLineContent={Content}
            onCancel={() => usecase.selectServices(null)}
            onConfirm={() => usecase.enable()}
            showOnlyOnError={(service) => !doesServiceNeedAuthorization(service)}
        />
    )
}
